import { useCallback, useEffect, useMemo, useState } from "react";
import { getProviders } from "services/paymentApi";
import {
  extractLocalPaymentData,
  LocalPaymentData,
  normalizeProvidersList,
} from "../helpers";
import { ProviderCode } from "../enums";
import { sanitizePaymentMethodsBrands } from "../utils/sanitizePaymentMethods";
import { parsePaymentMethodsFromBillingResponse } from "../utils/parsePaymentMethodsFromBilling";

interface UseDealerPaymentDataParams {
  dealerId?: string;
  paymentMethodsRaw: unknown;
}

export function useDealerPaymentData({
  dealerId,
  paymentMethodsRaw,
}: UseDealerPaymentDataParams) {
  const [localPaymentData, setLocalPaymentData] =
    useState<LocalPaymentData | null>(null);

  const paymentMethodsSanitized = useMemo(() => {
    const fromBilling =
      parsePaymentMethodsFromBillingResponse(paymentMethodsRaw);
    return sanitizePaymentMethodsBrands(fromBilling);
  }, [paymentMethodsRaw]);

  const fetchLocalPaymentProvider = useCallback(async () => {
    if (!dealerId) {
      setLocalPaymentData(null);
      return;
    }
    try {
      const response = await getProviders(dealerId);
      const list = normalizeProvidersList(response.data);
      const provider = list.find((item) => {
        if (!item || typeof item !== "object") return false;
        const code = String((item as Record<string, unknown>).code ?? "");
        return (
          code === ProviderCode.LocalPayment ||
          code === "LocalPayment" ||
          code === "LOCAL_PAYMENT"
        );
      });
      setLocalPaymentData(extractLocalPaymentData(provider) ?? null);
    } catch {
      setLocalPaymentData(null);
    }
  }, [dealerId]);

  useEffect(() => {
    fetchLocalPaymentProvider();
  }, [fetchLocalPaymentProvider]);

  return {
    localPaymentData,
    paymentMethodsSanitized,
    refetchLocalPayment: fetchLocalPaymentProvider,
  };
}
